import React, { useMemo } from "react";
import {
  GATES,
  isSequential,
  pins,
  nodeSize,
  pinPosition,
  pinLabel,
  signalName,
  netColor,
  activationFor,
  sourceKey,
} from "../engine/registry";
import {
  routeConnections,
  pathFromPoints,
  bounds,
  wireJunctions,
} from "../utils/routing";
const ink = "#26343b",
  accent = "#3e7c70";
export function GateShape({ type, w, h }) {
  const inv = ["NOT", "NAND", "NOR", "XNOR"].includes(type),
    end = w - (inv ? 8 : 0);
  return (
    <g fill="white" stroke={ink} strokeWidth="1.6" strokeLinejoin="round">
      {type === "NOT" ? (
        <path d={`M 0 0 L ${end} ${h / 2} L 0 ${h} Z`} />
      ) : ["AND", "NAND"].includes(type) ? (
        <path
          d={`M 0 0 H ${end * 0.46} C ${end * 1.17} 0 ${end * 1.17} ${h} ${end * 0.46} ${h} H 0 Z`}
        />
      ) : (
        <>
          <path
            d={`M 0 0 Q ${end * 0.6} ${-h * 0.06} ${end} ${h / 2} Q ${end * 0.6} ${h * 1.06} 0 ${h} Q ${end * 0.34} ${h / 2} 0 0 Z`}
          />
          {["XOR", "XNOR"].includes(type) && (
            <path
              d={`M -5 0 Q ${end * 0.34 - 5} ${h / 2} -5 ${h}`}
              fill="none"
            />
          )}
        </>
      )}
      {inv && <circle data-inversion="true" cx={w - 4} cy={h / 2} r="4" />}
    </g>
  );
}
export function NodeDrawing({
  node: c,
  project,
  selected,
  error,
  onNodeDown,
  onPinDown,
  onEdit,
  onEditPin,
}) {
  const { w, h } = nodeSize(c),
    ps = pins(c),
    seq = isSequential(c),
    gate = GATES.includes(c.type),
    constant = c.type.startsWith("CONST"),
    clock = c.type === "CLOCK";
  const bodyH = h - (c.type === "PA" && c.config.expanded ? 48 : 0);
  const activation = project
    ? activationFor(project, c)
    : { trigger: c.config.trigger, level: c.type === "SR_LATCH" };
  const font = Math.max(9, Math.min(13, w / 6));
  const hit = !!onNodeDown;
  return (
    <g transform={`translate(${c.x},${c.y})`} data-node={c.id}>
      {selected && (
        <rect
          x="-17"
          y="-8"
          width={w + 34}
          height={h + 16}
          rx="3"
          fill="none"
          stroke={accent}
          strokeDasharray="4 3"
          pointerEvents="none"
        />
      )}
      {error && <circle cx={w + 5} cy="-6" r="3" fill="#b85b55" />}
      <g
        onPointerDown={(e) => onNodeDown?.(e, c.id)}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onEdit?.(c.id);
        }}
        style={{ cursor: hit ? "move" : "default" }}
        data-testid={`node-body-${c.id}`}
      >
        {hit && (
          <rect
            x="-4"
            y="-4"
            width={w + 8}
            height={h + 8}
            fill="transparent"
            pointerEvents="all"
          />
        )}
        {gate ? (
          <GateShape type={c.type} w={w} h={h} />
        ) : (
          <rect
            width={w}
            height={bodyH}
            rx={seq ? 2 : 3}
            fill="white"
            stroke={ink}
            strokeWidth="1.5"
          />
        )}
        {constant && (
          <text
            x={w / 2}
            y={h / 2 + 5}
            textAnchor="middle"
            fill={ink}
            fontSize="16"
          >
            {c.type === "CONST1" ? "1" : "0"}
          </text>
        )}
        {clock && (
          <>
            <path
              d={`M 7 18 h 8 v -9 h 12 v 9 h 12`}
              fill="none"
              stroke={ink}
              strokeWidth="1.3"
            />
            <text
              x={w / 2}
              y={h - 4}
              textAnchor="middle"
              fill={ink}
              fontSize="9"
            >
              {c.config.activation?.endsWith("LEVEL")
                ? c.config.activation.startsWith("NEG")
                  ? "LOW"
                  : "HIGH"
                : c.config.activation?.startsWith("NEG")
                  ? "↓"
                  : "↑"}{" "}
              {c.name.toLowerCase()}
            </text>
          </>
        )}
        {!gate && !seq && !clock && !constant && (
          <text x={w / 2} y="15" textAnchor="middle" fill={ink} fontSize="10">
            {c.type === "COMPARE" ? "Comparator" : c.type}
          </text>
        )}
        {gate && c.config.showName && (
          <text x={w / 2} y="-8" textAnchor="middle" fontSize="9" fill={ink}>
            {c.name}
          </text>
        )}
        {c.type === "PA" && c.config.expanded && (
          <g>
            {Array.from({ length: c.config.width }, (_, i) => {
              const cell = (w - 8) / c.config.width,
                x = 4 + i * cell;
              return (
                <g key={i}>
                  <rect
                    x={x}
                    y={h - 38}
                    width={cell - 2}
                    height="22"
                    fill="#f2f6f3"
                    stroke={ink}
                    strokeWidth=".7"
                  />
                  <text
                    x={x + (cell - 2) / 2}
                    y={h - 23}
                    fontSize="8"
                    textAnchor="middle"
                  >
                    FA{i}
                  </text>
                  {i < c.config.width - 1 && (
                    <path d={`M ${x + cell - 2} ${h - 27} h 2`} stroke={ink} />
                  )}
                </g>
              );
            })}
            <text x={w / 2} y={h - 3} textAnchor="middle" fontSize="8">
              LSB → MSB
            </text>
          </g>
        )}
      </g>
      {["in", "out"].flatMap((direction) =>
        ps[direction === "in" ? "inputs" : "outputs"].map((pin) => {
          const p = pinPosition(c, pin.id, direction),
            x = p.x - c.x,
            y = p.y - c.y,
            input = direction === "in";
          const curve =
            gate && ["OR", "NOR", "XOR", "XNOR"].includes(c.type) && input
              ? w * 0.68 * (y / h) * (1 - y / h)
              : 0;
          const isControl = seq && pin.control;
          return (
            <g key={`${direction}:${pin.id}`}>
              <path
                d={`M ${x} ${y} H ${input ? curve : w}`}
                stroke={ink}
                strokeWidth="1.4"
                fill="none"
                pointerEvents="none"
              />
              {isControl && !activation.level && (
                <path
                  d={`M 0 ${y - 5} l 7 5 l -7 5`}
                  fill="none"
                  stroke={ink}
                  strokeWidth="1.3"
                  pointerEvents="none"
                />
              )}
              {isControl && activation.trigger === "NEG" && (
                <circle
                  cx="-4"
                  cy={y}
                  r="3"
                  fill="white"
                  stroke={ink}
                  pointerEvents="none"
                />
              )}
              {!gate && !clock && !constant && (
                <text
                  x={input ? (isControl ? 10 : 6) : w - 6}
                  y={y + 4}
                  textAnchor={input ? "start" : "end"}
                  fill={ink}
                  fontSize={seq ? font : 10}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    onEditPin?.(c.id, pin.id);
                  }}
                  style={{ cursor: onEditPin ? "text" : "default" }}
                >
                  {pinLabel(c, pin.id)}
                </text>
              )}
              {hit && (
                <circle
                  cx={x}
                  cy={y}
                  r="9"
                  fill="transparent"
                  pointerEvents="all"
                  data-pin={`${c.id}:${pin.id}`}
                  data-direction={direction}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    onPinDown?.(e, c.id, pin.id, direction);
                  }}
                  style={{ cursor: "crosshair" }}
                />
              )}
              {gate && !input && c.config.pinLabels?.[pin.id] && <text x={w + 15} y={y - 5} fontSize="10" fill={ink} onDoubleClick={e => {e.stopPropagation(); onEditPin?.(c.id, pin.id);}}>{pinLabel(c, pin.id)}</text>}
            </g>
          );
        }),
      )}
    </g>
  );
}
export function CircuitContents({
  project,
  selected = [],
  selectedWire,
  errorIds = [],
  onNodeDown,
  onPinDown,
  onWireDown,
  onBranch,
  onEdit,
  onEditPin,
  onTerminalEdit,
  onTerminalDown,
}) {
  const routes = useMemo(() => routeConnections(project), [project]);
  const junctions = useMemo(() => wireJunctions(routes), [routes]);
  const terminals = []; const labeled = new Set();
  for (const r of routes) { const key = sourceKey(r.e.from); if ((r.e.from.input || "constant" in r.e.from) && r.points.length && !labeled.has(key)) {labeled.add(key); terminals.push(r);} }
  return (
    <g fontFamily="Arial, sans-serif" style={{ userSelect: "none" }}>
      {routes.map(({ e, points }) => {
        if (!points.length) return null;
        const color = e.color || netColor(project, e.from);
        return (
          <g key={e.id}>
            <path
              d={pathFromPoints(points)}
              stroke={selectedWire === e.id ? accent : color}
              strokeWidth={selectedWire === e.id ? 2.4 : 1.5}
              strokeLinejoin="round"
              fill="none"
              pointerEvents="none"
            />
            {onWireDown && (
              <path
                d={pathFromPoints(points)}
                fill="none"
                stroke="transparent"
                strokeWidth="10"
                data-wire={e.id}
                pointerEvents="stroke"
                onPointerDown={(evt) => {
                  evt.stopPropagation();
                  onWireDown(evt, e.id);
                }}
                onDoubleClick={(evt) => {
                  evt.stopPropagation();
                  onBranch?.(e.from);
                }}
                style={{ cursor: "pointer" }}
              />
            )}

          </g>
        );
      })}
      {terminals.map(({e, points}) => { const point = points[0], color = netColor(project, e.from); return <g key={sourceKey(e.from)}>
        <rect x={point.x - 2} y={point.y - 2} width="4" height="4" fill={color} pointerEvents="none" />
        {onTerminalDown && <rect data-terminal-source={e.from.input} aria-label={`Connect ${signalName(project, e.from)}`} x={point.x - 8} y={point.y - 7} width="18" height="14" fill="transparent" style={{cursor:'crosshair'}} onPointerDown={evt => {evt.stopPropagation(); onTerminalDown(evt,e.from);}}><title>Drag to an input pin to connect this terminal</title></rect>}
        <text data-terminal={e.from.input} x={point.x - 6} y={point.y + 3.5} textAnchor="end" fontSize="10" fill={color} style={{cursor:onTerminalEdit?'text':'default'}} onPointerDown={evt=>evt.stopPropagation()} onDoubleClick={evt=>{evt.stopPropagation(); if(e.from.input)onTerminalEdit?.(e.from.input);}}>{signalName(project,e.from)}</text>
      </g>;})}
      {junctions.map(({point,source},i)=><circle key={`junction-${i}`} cx={point.x} cy={point.y} r="2.4" fill={netColor(project,source)} pointerEvents="none" />)}
      {project.components.map((c) => (
        <NodeDrawing
          key={c.id}
          node={c}
          project={project}
          selected={selected.includes(c.id)}
          error={errorIds.includes(c.id)}
          {...{ onNodeDown, onPinDown, onEdit, onEditPin }}
        />
      ))}
    </g>
  );
}
export function CircuitSVG({ project, ...rest }) {
  const b = bounds(project);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={b.w}
      height={b.h}
      viewBox={`${b.x} ${b.y} ${b.w} ${b.h}`}
      role="img"
      aria-label="Circuit schematic"
    >
      <rect x={b.x} y={b.y} width={b.w} height={b.h} fill="white" />
      <CircuitContents project={project} {...rest} />
    </svg>
  );
}
